import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, Copy, DownloadSimple, Share, IdentificationBadge } from "@phosphor-icons/react";
import { toast } from "sonner";
import { buildVCard, downloadVCard } from "../lib/vcard";

/**
 * Large, tap-target-friendly QR code modal.
 *   - Two modes: 'vcard' (default — scanning it prompts iOS/Android to save
 *     the contact directly) and 'url' (opens the public card page).
 *   - Meant for use from the Dashboard so users can flash their QR at anyone
 *     mid-conversation without having to navigate away.
 */
export default function QrModal({ open, onClose, user }) {
  const [mode, setMode] = useState("vcard");
  if (!open || !user) return null;
  const p = user.profile || {};
  const name = p.name || user.name;
  const publicUrl = `${window.location.origin}/c/${user.slug}`;
  const vCard = buildVCard({
    name,
    email: user.email,
    phone: p.phone,
    title: p.title,
    company: p.company,
    website: p.website,
    linkedin: p.linkedin,
    tagline: p.tagline,
  });
  const value = mode === "vcard" ? vCard : publicUrl;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Card link copied");
    } catch {
      toast.error("Copy failed");
    }
  };
  const downloadVcf = () => {
    downloadVCard(vCard, `${(name || "card").replace(/[^\w\-]+/g, "_")}.vcf`);
    toast.success("Contact file downloaded");
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: name, text: "Save my contact info", url: publicUrl });
      } catch { /* user canceled */ }
    } else {
      copyLink();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.86)", backdropFilter: "blur(8px)" }}
      data-testid="qr-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md border relative my-auto"
        style={{ background: "var(--surface-1)", borderColor: "var(--accent)" }}
      >
        <button
          onClick={onClose}
          data-testid="qr-modal-close"
          className="absolute top-3 right-3 p-2 hover:bg-[#15303A] z-10"
          aria-label="Close"
        >
          <X weight="bold" />
        </button>
        <div className="p-6 sm:p-8">
          <div className="overline mb-2" style={{ color: "var(--accent)" }}>// your qr code</div>
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mb-1 break-words">{name}</h2>
          <p className="text-xs sm:text-sm mb-5" style={{ color: "var(--text-2)" }}>
            {mode === "vcard"
              ? "Point any iPhone or Android camera at this — their phone will prompt to save your contact directly."
              : "Scanning opens your full digital card page with bio + referral form."}
          </p>

          {/* Mode toggle */}
          <div className="flex gap-2 mb-5" data-testid="qr-modal-mode">
            <button
              onClick={() => setMode("vcard")}
              data-testid="qr-modal-mode-vcard"
              className="tag cursor-pointer flex-1"
              style={mode === "vcard" ? { borderColor: "var(--accent)", color: "var(--accent)" } : {}}
            >
              Add to contacts
            </button>
            <button
              onClick={() => setMode("url")}
              data-testid="qr-modal-mode-url"
              className="tag cursor-pointer flex-1"
              style={mode === "url" ? { borderColor: "var(--accent)", color: "var(--accent)" } : {}}
            >
              Open card page
            </button>
          </div>

          {/* Large QR — fills width on small screens */}
          <div className="flex items-center justify-center bg-white p-5 sm:p-6 mb-4" data-testid="qr-modal-code">
            <QRCodeSVG
              value={value}
              size={280}
              bgColor="#FFFFFF"
              fgColor="#0A1418"
              level="M"
              style={{ width: "100%", height: "auto", maxWidth: 320 }}
            />
          </div>

          <div className="grid grid-cols-3 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
            <button onClick={copyLink} data-testid="qr-modal-copy" className="btn-secondary flex items-center justify-center gap-1 py-3 border-l-0 border-t-0 text-xs">
              <Copy weight="bold" size={14} /> Link
            </button>
            <button onClick={downloadVcf} data-testid="qr-modal-vcf" className="btn-secondary flex items-center justify-center gap-1 py-3 border-t-0 text-xs">
              <DownloadSimple weight="bold" size={14} /> .vcf
            </button>
            <button onClick={nativeShare} data-testid="qr-modal-share" className="btn-secondary flex items-center justify-center gap-1 py-3 border-t-0 text-xs">
              <Share weight="bold" size={14} /> Share
            </button>
          </div>

          <a
            href={`/c/${user.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid="qr-modal-open-card"
            className="mt-4 flex items-center justify-center gap-2 text-xs uppercase tracking-widest font-mono hover:text-white"
            style={{ color: "var(--text-2)" }}
          >
            <IdentificationBadge weight="bold" /> preview my public card
          </a>
        </div>
      </div>
    </div>
  );
}
